/* Qualidade: as invariantes I1..I10, que só existiam como saída de terminal. */

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { compact, fmtDateTime, intBR, pct } from "../lib/format";
import { Card, ErrorState, PageHead, Skeleton, Tiles } from "../components/ui";

interface Invariant {
  id: string; name: string; status: string; detail: string; corpus_bound: string | null;
}

export function Quality() {
  const invariants = useQuery({
    queryKey: ["invariants"],
    queryFn: () => api<{ ran_at: string; ran: boolean; invariants: Invariant[];
                         failed: number; warned: number }>("/api/quality/invariants"),
  });
  const reconciliation = useQuery({
    queryKey: ["reconciliation"],
    queryFn: () => api<{ by_flow: { flow: string; mass: number; rows: number }[];
                         gross: number; sum_of_parts: number; reconciles: boolean }>(
      "/api/quality/reconciliation"),
  });
  const stages = useQuery({
    queryKey: ["stages"],
    queryFn: () => api<{ stage: string; rows: number; mass: number }[]>("/api/quality/stages"),
  });
  const outros = useQuery({
    queryKey: ["outros"],
    queryFn: () => api<{ category: string; mass: number; rows: number;
                         pct_of_gross: number }[]>("/api/quality/outros"),
  });

  const badge = (status: string) =>
    status === "ok" ? { text: "ok", color: "var(--receita)" }
    : status === "warn" ? { text: "aviso", color: "var(--despesa)" }
    : { text: "falhou", color: "var(--despesa)" };

  return (
    <>
      <PageHead eyebrow="invariantes I1..I10" title="Qualidade dos dados">
        Cada invariante existe porque um bug real passou por ela. Elas são o que impede o
        número de mentir — e o que torna o total confiável.
      </PageHead>

      {reconciliation.data && (
        <Tiles items={[
          { key: "bruto", label: "Massa bruta", unit: "BRL", value: reconciliation.data.gross },
          { key: "recon", label: "Reconciliação (I4)", unit: "n",
            value: reconciliation.data.reconciles ? 1 : 0,
            detail: reconciliation.data.reconciles
              ? "soma dos prefixos bate ao centavo"
              : "DIVERGE — investigar" },
          { key: "falhas", label: "Invariantes falhando", unit: "n",
            value: invariants.data?.failed ?? null,
            flow: invariants.data?.failed ? "DESPESA" : undefined },
          { key: "avisos", label: "Avisos", unit: "n", value: invariants.data?.warned ?? null },
        ]} />
      )}

      <div style={{ marginTop: 18 }}>
        <Card title="Invariantes"
              sub={invariants.data ? `medidas em ${fmtDateTime(invariants.data.ran_at)}` : undefined}>
          {invariants.isPending && <Skeleton rows={9} />}
          {invariants.error && (
            <ErrorState error={invariants.error} onRetry={() => invariants.refetch()} />
          )}
          {invariants.data && (
            <table>
              <thead>
                <tr>
                  <th scope="col">id</th>
                  <th scope="col">situação</th>
                  <th scope="col">o que garante</th>
                </tr>
              </thead>
              <tbody>
                {invariants.data.invariants.map((row) => {
                  const b = badge(row.status);
                  return (
                    <tr key={row.id}>
                      <td className="mono">{row.id}</td>
                      <td><span className="tag" style={{ borderColor: b.color, color: b.color }}>
                        {b.text}
                      </span></td>
                      <td>
                        {row.name}
                        {row.detail && (
                          <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 3 }}>
                            {row.detail}
                          </div>
                        )}
                        {row.corpus_bound && (
                          <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 3,
                                        fontStyle: "italic" }}>
                            Presa a este corpus: {row.corpus_bound}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 18, display: "grid", gap: 18,
                    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))" }}>
        <Card title="Onde a cascata decidiu" sub="etapa que classificou cada linha">
          {stages.isPending && <Skeleton rows={6} />}
          {stages.data && (
            <table>
              <thead>
                <tr><th scope="col">etapa</th><th scope="col" className="num">linhas</th>
                    <th scope="col" className="num">massa</th></tr>
              </thead>
              <tbody>
                {stages.data.map((row) => (
                  <tr key={row.stage}>
                    <td className="mono">{row.stage}</td>
                    <td className="num">{intBR(row.rows)}</td>
                    <td className="num">R$ {compact(row.mass)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Progresso (I10)" sub="massa ainda parada nas filas OUTROS">
          {outros.isPending && <Skeleton rows={3} />}
          {outros.data && (
            <table>
              <thead>
                <tr><th scope="col">categoria</th><th scope="col" className="num">massa</th>
                    <th scope="col" className="num">% da bruta</th></tr>
              </thead>
              <tbody>
                {outros.data.map((row) => (
                  <tr key={row.category}>
                    <td>{row.category}</td>
                    <td className="num">R$ {compact(row.mass)}</td>
                    <td className="num">{row.pct_of_gross}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 18 }}>
        <Card title="Reconciliação por tipo de fluxo (I4)"
              sub="a soma dos três prefixos tem de bater com a massa bruta, ao centavo">
          {reconciliation.data && (
            <table>
              <thead>
                <tr><th scope="col">fluxo</th><th scope="col" className="num">linhas</th>
                    <th scope="col" className="num">massa</th>
                    <th scope="col" className="num">% da bruta</th></tr>
              </thead>
              <tbody>
                {reconciliation.data.by_flow.map((row) => (
                  <tr key={row.flow}>
                    <td>{row.flow}{row.flow === "MOVIMENTACAO" && (
                      <span className="tag" style={{ marginLeft: 8 }}>fora do total</span>)}</td>
                    <td className="num">{intBR(row.rows)}</td>
                    <td className="num">R$ {compact(row.mass)}</td>
                    <td className="num">{pct(row.mass / reconciliation.data!.gross)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 600 }}>
                  <td>soma</td><td /><td className="num">
                    R$ {compact(reconciliation.data.sum_of_parts)}
                  </td>
                  <td className="num">{reconciliation.data.reconciles ? "✓" : "✗"}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}
